import { forEach, compact, invokeMap, find, last } from 'lodash';

import { OwnedObject, Magnitude, clone, cloneDeep, concat } from 'gear';

import { Point, Matrix, Bounds, Frame, Segment } from '../../geometry';

import { Shape, GShape, Region, Polyregion } from '../shape';
import { AbsoluteAnchor } from '../anchor';
import { PowerClipLink } from '../power-clip';

import { TextChar } from './text-char';

// engine @ Engine/Figure/CdlTextBlock

// parentText.CreateTextFigureForPart( textPart ) => text
// textPart.asTextBlock( parentText ) => text

// InputAnchor {
//  if( IsSimple ) return SingleInputTextFigure.Anchor ;
//  else           return base.InputAnchor ;
//}


// engine @ Engine/Figure/TextCharData

export const TextCharData = OwnedObject.extend({ typeName: 'TextCharData',

  init() {
    this._extraKerning = 0;
    this._transformers = [];
  },

  clone() {
    console.log('clone TextCharData');
    const c = TextCharData();
    c._extraKerning = this._extraKerning;
    c._transformers = cloneDeep(this._transformers);
    return c;
  }
});


function createTextPolyregion( regions, text ) {
  return new Polyregion({
    regions: regions, brush: text._brush, pen: text._pen,
    powerClipId: text.powerClipId, powerClipLink: text._powerClipLink,
    fillMode: 'Winding'
  });
}

// engine @ Engine/Figure/TextFigure
// engine @ Engine/Figure/TextLayout
// engine @ Engine/Figure/TextLine

export const TextLine = OwnedObject.extend({ typeName: 'TextLine',

  init( chars,baseline) {
    this._chars = chars;
    this._baseline = baseline;

    const bounds = this.bounds;
    if( bounds ) {
      bounds.ly = baseline.P0.y;

      this._ascentFrame = Frame.fromBounds(bounds);
      // this._stableFrame =
    }
    else {
      this._ascentFrame = null;
    }
  },

  clone() {
    const c = new TextLine();
    c._chars = cloneDeep(this._chars);
    c._baseline = cloneDeep(this._baseline);
    c._ascentFrame = clone(this._ascentFrame);
    return c;
  },

  transformBasicPoints( t ) {
    forEach( this._chars, c => {
      c.transformBasicPoints(t);
    });
    this._baseline.transform(t);
  },
  transform( t ) {
    forEach( this._chars, c => {
      c._charData._transformers.push( new TextChar.Transformer(t) );
      c.transform(t);
    });
    this._baseline.transform(t);
  },

  bearingLX() {
    const fc = this._chars[0];
    return fc ? fc.bearingLX() : 0;
  },

  get bounds() {
    const regions = this.regions;
    return regions.length > 0 ? Bounds.ofThings(regions) : null;
  },

  // Return nulls in the glyphs that are not found in the font
  allChars() {
    const ac = [];
    const chars = this._chars;
    for(let k=0, kEnd = chars.length; k < kEnd; ++k ) {
      const ck = chars[k];

      let missing = ck._originalIdx-1 - ac.length;
      while( missing > 0 ) {
        ac.push(null);
        missing--;
      }
      ac.push( chars[k] );
    }
    return ac;
  },

  get regions() {
    const regions = [];
    forEach( this._chars, char => {
      forEach( char._outlines, o => {
        regions.push( new Region([ o.clone() ]) );
      });

      /*
      if( char._shape ) {
        _.forEach( char._shape._multiregions, mr => {
          _.forEach( mr._regions, r => {
            regions.push( r.clone() );
          });
        });
      }
      */
    });
    return regions;
  },

  polyregion( text ) {
    const regions = this.regions;
    if( regions.length === 0 ) {
      return null;
    }

    const mr = createTextPolyregion(regions,text);

    mr._renderFrame = Frame.fromBounds( mr.bounds );
    return mr;
  },

  perCharPolyregions( text ) {

    const polyregions = [];

    forEach( this._chars, char => {

      const regions = [];
      forEach( char._outlines, o => {
        regions.push( new Region([ o.clone() ]) );
      });

      if( regions.length > 0 ) {
        polyregions.push( createTextPolyregion(regions,text) );
      }

    });
    return polyregions;
  }

});

export const TextLayout = OwnedObject.extend({ typeName: 'TextLayout',

  get isTextLayout() { return true; },

  init( text ) {
    this._text = text;

    // Dictionary LineIdx,CharId => CharData
    this._charData = { 0: {} };

    this._textLines = null; // array of TextLine

    this._transform = new Matrix();
  },

  clone() {
    const tf = new TextLayout();

    const rep = this._textLines;
    tf._textLines = rep ? cloneDeep(rep) : null;

    tf._charData = cloneDeep( this._charData );

    tf._transform = this._transform.clone();

    return tf;
  },

  charData(  lineIdx, charIdx ) {
    const charData = this._charData;
    let entry = charData[lineIdx];
    if( ! entry ) {
      entry = charData[lineIdx] = {};
    }
    let cd = entry[charIdx];
    if( ! cd ) {
      cd = entry[charIdx] = new TextCharData();
    }
    return cd;
  },

  get text() { return this._text; },

  get polyregions() {
    const text = this._text;

    let polyregions = [];
    forEach( this.textLines(), textLine => {
      if( text.perCharPolyregions ) {
        polyregions = concat(polyregions, textLine.perCharPolyregions(text));
      }
      else {
        polyregions.push( textLine.polyregion(text) );
      }
    });

    polyregions = compact(polyregions);

    const transform = this._transform;
    if( ! transform.isIdentity() ) {
      invokeMap(polyregions,'transform',transform);
    }

    return polyregions;
  },

  shape() {
    // console.log('>> shape');
    const textLines = this.textLines(), text = this._text;
    if( textLines.length > 0 ) {
      const shape = Shape({ polyregions: this.polyregions });

      shape.anchor = AbsoluteAnchor( this.prePin() );
      shape.alpha = text.alpha;
      return shape;
    }
    else{
      return text.createEmptyOutput();
    }
  },

  toGShape() {
    const text = this._text, powerClipContents = text._powerClipContents;
    const polyregions = this.polyregions;
    forEach( polyregions, p => {
      const pcId = p._powerClipId;
      if( pcId && ! p._powerClipLink ) {
        const pcContent = find(powerClipContents,{_id:pcId});
        if( pcContent ) {
          p._powerClipLink = new PowerClipLink({content:pcContent});
        }
      }
    });
    return new GShape({ polyregions: polyregions });
  },

  prePin() {
    // We need to discuss if this is the best way to define the text anchor
    const textLines = this.textLines(), text = this._text;
    const anchorLine = find(textLines,'_ascentFrame');
    if( anchorLine ) {
      return text._anchor.of( anchorLine._ascentFrame );
    }
    else {
      return new Point(0,0);
    }
  },

  spaceWidth() {
    return this._text.spaceWidth_();
  },
  ascent() {
    return this._text.ascent_();
  },
  descent() {
    return this._text.descent_();
  },
  sizeOfReferenceChar() {
    return this._text.sizeOfReferenceChar_();
  },
  isWidthwise() {
    return this._text.isWidthwise();
  },
  pivot() {
    const eld = this._text.effectiveLineDirection();
    return Math.abs(eld.x) >= Math.abs(eld.y) ? eld.x > 0 ? 'Grip'             : 'BaselineRightGrip' :
                                                eld.y > 0 ? 'BottomCenterGrip' : 'TopCenterGrip' ;
  },
  invalidate() {
    this._textLines = null;
  },

  textLines() {
    if( ! this._textLines ) {
      this._textLines = this.createTextLines();
    }
    return this._textLines;
  },

  charAt( lineIdx,idx) {
    const textLines = this.textLines();
    if( lineIdx >= 1 && lineIdx <= textLines.length ) {
      const lineChars = textLines[lineIdx-1]._chars;
      if( idx >= 1 && lineIdx <= lineChars.length ) {
        return lineChars[idx-1];
      }
    }
    return null;
  },

  createChars() {
    const _this = this,
          text = _this._text,
          fontData = text._fontData,
          glyphs = [];


    let lines = [];

    const text_lines = text._fontDataLines; // Used a cached version of the lines
    if( text.doMultilineVerticalText() ) {
      const singleVerticalLine = text_lines[0];
      forEach( singleVerticalLine, char => {
        lines.push(char);
      });
    }
    else {
      lines = text_lines;
    }

    let maxLineLength = 0;

    forEach( lines, lineString => {

      if( lineString.length > 0 ) {
        const lineGlyphs = fontData.createGlyphs( lineString, text.fontDataScale_(),
                                                text.extraCharSpacing, text.extraWordSpacing,
                                                text.effectiveLineDirection(), text.correctZeroAdvance() );

        if( lineGlyphs.length > 0 ) {
          const lastGlyph = last(lineGlyphs);
          const thisLineLength = lastGlyph._hPos + lastGlyph._advanceX;
          if( thisLineLength > maxLineLength ) {
            maxLineLength = thisLineLength;
          }
        }
        glyphs.push( lineGlyphs );
      }
      else {
        // Empty line
        glyphs.push( [] );
      }
    });

    const linesAlign = text.linesAlign;
    if( glyphs.length > 1 && linesAlign !== 'Left' ) {
      forEach( glyphs, lineGlyphs => {
        if( lineGlyphs.length > 0 ) {
          const lastGlyph = last(lineGlyphs);
          const lineLength = lastGlyph._hPos + lastGlyph._advanceX;
          const maxLineDiff = maxLineLength - lineLength;

          const offset = linesAlign === 'Center' ? maxLineDiff/2 :
                       linesAlign === 'Right'  ? maxLineDiff   : 0 ;

          forEach( lineGlyphs, glyph => {
            glyph._hPos += offset;
          });
        }
      });
    }

    // Maybe we can fusion glyph and TextChar
    const chars = [];
    forEach( glyphs, (thisLineGlyphs,ln) => {
      const thisLineChars = [];
      chars.push(thisLineChars);
      const lineIdx = ln + 1;
      forEach( thisLineGlyphs, (glyph,cn) => {
        const idx = cn + 1;
        thisLineChars.push( TextChar.fromGlyph(glyph,lineIdx,idx,_this.charData(lineIdx,idx) ));
      });
    });
    return chars;
  },

  createTextLines() {
    const _this = this,
        chars = _this.createChars(),
        text = _this._text,
        fontData = text._fontData,
        fontDataScale = text.fontDataScale_();

    const effectiveExtraLineSpacing = text.effectiveExtraLineSpacing_(),
        effectiveLinesPerColumn = text.effectiveLinesPerColumn(chars.length),
        effectiveLineDirection = text.effectiveLineDirection(),
        absoluteColumnSpacing = text.columnSpacing?Magnitude.absoluteMMValueFor(text.columnSpacing, fontData.spaceWidth(fontDataScale)):0,
        pivot = _this.pivot();

    let lineIndexInColumn = 0,
        baselineS = new Point(0,0),
        nextColumnBaselineS = new Point(0,0),
        nextLineV = effectiveLineDirection.perpendicular(-1).sm( fontData.lineSpacing(fontDataScale) + effectiveExtraLineSpacing );

    const lines = [];

    forEach( chars, (charsInThisLine,ln) => {
      const lineIdx = ln+1;
      if( charsInThisLine.length > 0 ) {

        let accExtrakerning = 0;
        forEach( charsInThisLine, (char,cn) => {
          const idx = cn+1;
          const data = _this.charData(lineIdx,idx);

          const extrakerning = data._extraKerning;
          const newHPos = char._originalHPos + accExtrakerning + extrakerning;
          char.placeAt( pivot, baselineS .plus( effectiveLineDirection.scaled(newHPos) ) );
          char._hPos = newHPos;

          forEach( data._transformers, tct => {
            tct.transform(char);
          });

          accExtrakerning += extrakerning;
        });

        const lastChar = last(charsInThisLine);

        const baselindeT = lastChar.namedPoint(pivot) .plus( lastChar.computedSlantedAdvance(effectiveLineDirection) );

        const baseline = new Segment( baselineS.clone(), baselindeT );

        if( lineIndexInColumn === 0 ) {
          nextColumnBaselineS = baselindeT.clone();
        }

        if( baselindeT.x > nextColumnBaselineS.x ) {
          nextColumnBaselineS.x = baselindeT.x ;
        }

        lines.push( new TextLine( charsInThisLine, baseline ) );
      }
      else {
        lines.push( new TextLine( [], new Segment( baselineS.clone(), baselineS .plus( effectiveLineDirection ) ) ) );
      }

      ++lineIndexInColumn;

      if( effectiveLinesPerColumn !== null && lineIndexInColumn >= effectiveLinesPerColumn ) {
        lineIndexInColumn = 0 ;
        baselineS = nextColumnBaselineS .plus( effectiveLineDirection.scaled( absoluteColumnSpacing ) );
      }
      else {
        baselineS = baselineS .plus( nextLineV );
      }
    });

    return lines;
  },

  transformBasicPoints( t ) {
    forEach( this.textLines(), line => {
      line.transformBasicPoints(t);
    });
  },

  // [IMPORTANT]
  // This function is only used by pretransform, and is a copy of what the engine does
  // The pre transform is not really applied before all the processes, but after the ones
  // that output texts (FitOnPath, BookEnd, etc).
  // So this transform is kept and is applied when toGShape is called
  // This actually breaks when using PreTransform and FitOnPath.
  // We should fix this, but I do not know if we should care about a feature that is
  // not well defined and is not exposed in current designers
  transform( t ) {
    this._transform = Matrix.m(t,this._transform);
  }
});



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/text/text-layout.js
 **/